// Library: the user's persistent, isolated history. Every generated question is
// here (written server-side at generation time); "saved" ones can be filtered.
// All reads are uid-scoped via listLibrary. Answering a question here updates it
// in place, so the Library doubles as a review surface across sessions.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useExam } from '../context/ExamContext';
import { listLibrary } from '../lib/db';
import QuestionCard from '../components/QuestionCard';

export default function Library() {
  const { user } = useAuth();
  const { exams } = useExam();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examFilter, setExamFilter] = useState('all');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    void listLibrary(user.uid)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [user]);

  function patchItem(next) {
    setItems((prev) => prev.map((it) => (it.itemId === next.itemId ? next : it)));
  }

  const visible = useMemo(() => {
    return items.filter((it) => {
      if (examFilter !== 'all' && it.examId !== examFilter) return false;
      if (filter === 'saved' && !it.saved) return false;
      if (filter === 'answered' && !it.response) return false;
      return true;
    });
  }, [items, examFilter, filter]);

  const examName = (id) => exams.find((e) => e.id === id)?.name ?? id;

  return (
    <>
      <div className="page-head">
        <span className="eyebrow">Library</span>
        <h1>Saved &amp; generated</h1>
        <p>Everything you've generated, persisted to your account. Filter by exam or status.</p>
      </div>

      <div className="card">
        <div className="row between wrap">
          <div className="row wrap" style={{ gap: '0.4rem' }}>
            {['all', 'saved', 'answered'].map((f) => (
              <button
                key={f}
                className={`btn btn-sm${filter === f ? ' btn-primary' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f[0].toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          <select
            className="field"
            style={{ maxWidth: 240 }}
            value={examFilter}
            onChange={(e) => setExamFilter(e.target.value)}
          >
            <option value="all">All exams</option>
            {exams.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading library…</div>
      ) : visible.length === 0 ? (
        <div className="empty" style={{ marginTop: '1.5rem' }}>
          {items.length === 0
            ? 'Your library is empty. Generate questions in the Workspace to fill it.'
            : 'No items match these filters.'}
        </div>
      ) : (
        <div className="stack" style={{ marginTop: '1.5rem' }}>
          <div className="muted" style={{ fontSize: '0.85rem' }}>
            {visible.length} item{visible.length === 1 ? '' : 's'}
            {examFilter !== 'all' && ` · ${examName(examFilter)}`}
          </div>
          {visible.map((it) => (
            <QuestionCard key={it.itemId} item={it} onChanged={patchItem} />
          ))}
        </div>
      )}
    </>
  );
}
